<template>
  <div>
    <div id="reports-page" class="form-links px-i">
      <div class="form-links-wrapper container-fluid">
        <router-link :to="{ name: 'Edit Customer', params: {
                       id: customer.id }
                     }"
                     role="button"
                     class="btn btn-primary ihjoz-button form-links-wrapper-item">
          <div class="form-links-wrapper-item-icon">
            <i class="demo-icon icon-pencil" />
          </div>
          <span class="form-links-wrapper-item-text">Edit</span>
        </router-link>
        <button type="button"
                class="btn btn-info ihjoz-button form-links-wrapper-item"
                data-toggle="modal"
                data-target="#delete-customer-modal">
          <div class="form-links-wrapper-item-icon">
            <i class="demo-icon icon-ban" />
          </div>
          <span class="form-links-wrapper-item-text">Delete</span>
        </button>
      </div>
    </div>
    <div id="delete-customer-modal"
         class="modal fade"
         tabindex="-1"
         role="dialog"
         aria-labelledby="delete-customer-modal-label"
         aria-hidden="true">
      <div class="modal-dialog custom-modal">
        <div class="modal-content">
          <div class="modal-header custom-modal-header px-i">
            <div class="custom-modal-header-start">
              <h4 class="modal-title custom-modal-header-start-title">
                Delete Customer
              </h4>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div class="modal-body custom-modal-body px-i font-weight-light">
            <p>By deleting this customer, all information about the customer will be deleted from the system.</p>
            <p>Invoices previously issued to this customer will still contain customer details but will no longer be linked to this customer profile.</p>
            <p>This action cannot be reverted!</p>
          </div>
          <div class="modal-footer custom-modal-footer px-i">
            <button type="button"
                    class="btn btn-outline-primary ihjoz-button"
                    data-dismiss="modal">
              Cancel
            </button>
            <button type="button"
                    class="btn btn-primary ihjoz-button"
                    data-dismiss="modal"
                    @click="deleteCustomer()">
              Delete Customer
            </button>
          </div>
        </div>
      </div>
    </div>
    <FormCard :show-buttons="false">
      <CardForm :resource="customer"
                resource-name="customer"
                :section-title="`${customer.display_name}`">
        <div class="customer-info">
          <template v-if="customerFullName">
            <div class="customer-info-row">
              <div class="customer-info-row-start">
                <i class="demo-icon icon-user customer-info-row-start-icon" />
                <p class="customer-info-row-start-text">
                  {{ customerFullName }}
                </p>
              </div>
            </div>
          </template>
          <template v-if="customer.company_name">
            <CardDivider />
            <div class="customer-info-row">
              <div class="customer-info-row-start">
                <i class="demo-icon icon-building customer-info-row-start-icon" />
                <p class="customer-info-row-start-text">
                  {{ customer.company_name }}
                </p>
              </div>
            </div>
          </template>
          <template v-if="customer.mobile_number">
            <CardDivider />
            <div class="customer-info-row">
              <div class="customer-info-row-start">
                <i class="demo-icon icon-phone customer-info-row-start-icon" />
                <p class="customer-info-row-start-text font-weight-normal">
                  {{ customer.mobile_number }}
                </p>
              </div>
              <div class="customer-info-row-end">
                <a type="button"
                   class="btn btn-primary ihjoz-button"
                   :href="`tel:${customer.mobile_number}`">Call</a>
              </div>
            </div>
          </template>
          <template v-if="customer.email">
            <CardDivider />
            <div class="customer-info-row">
              <div class="customer-info-row-start">
                <i class="demo-icon icon-enveloppe customer-info-row-start-icon" />
                <p class="customer-info-row-start-text font-weight-normal">
                  {{ customer.email }}
                </p>
              </div>
              <div class="customer-info-row-end">
                <a type="button"
                   class="btn btn-primary ihjoz-button"
                   :href="`mailto:${customer.email}`">Email</a>
              </div>
            </div>
          </template>
          <div class="customer-info-table px-i">
            <div class="customer-info-table-cell col-24 col-md-12">
              <h3 class="customer-info-table-cell-title">
                Bill to
              </h3>
              <p class="customer-info-table-cell-subtitle">
                {{ customer.display_name }}
              </p>
              <p v-if="customer.billing_address.address"
                 v-raw="customer.billing_address.address"
                 class="customer-info-table-cell-text" />
              <p v-if="showBillToAddress" class="customer-info-table-cell-text">
                {{ displayAddress(customer.billing_address.zip_code,
                                  customer.billing_address.city,
                                  customer.billing_address.country) }}
              </p>
              <p v-if="customer.billing_address.phone_number" class="customer-info-table-cell-text">
                {{ customer.billing_address.phone_number }}
              </p>
              <p v-if="customer.billing_address.attention" class="customer-info-table-cell-text">
                Attention: {{ customer.billing_address.attention }}
              </p>
            </div>
            <div v-if="customerHasShippingInfo" class="customer-info-table-cell col-24 col-md-12">
              <h3 class="customer-info-table-cell-title">
                Ship to
              </h3>
              <p class="customer-info-table-cell-subtitle">
                {{ customer.display_name }}
              </p>
              <p v-if="customer.shipping_address.address"
                 v-raw="customer.shipping_address.address"
                 class="customer-info-table-cell-text" />
              <p v-if="showShipToAddress" class="customer-info-table-cell-text">
                {{ displayAddress(customer.shipping_address.zip_code,
                                  customer.shipping_address.city,
                                  customer.shipping_address.country) }}
              </p>
              <p v-if="customer.shipping_address.phone_number"
                 class="customer-info-table-cell-text">
                {{ customer.shipping_address.phone_number }}
              </p>
              <p v-if="customer.shipping_address.attention" class="customer-info-table-cell-text">
                Attention: {{ customer.shipping_address.attention }}
              </p>
            </div>
            <div v-else class="customer-info-table-cell col-24 col-md-12">
              <h3 class="customer-info-table-cell-title">
                Ship to
              </h3>
              <router-link :to="{ name: 'Edit Customer', params: {
                             id: customer.id }
                           }"
                           class="customer-info-table-cell-subtitle d-block">
                Add new address
              </router-link>
            </div>
          </div>
        </div>
      </CardForm>
    </FormCard>

    <div class="page-divider" />

    <InvoiceReportTable :columns="columns"
                        :items="items"
                        :show-form-toggle="false" />
  </div>
</template>

<script>
import FormCard from "../../../shared/components/UI/FormCard.vue";
import CardForm from "../../../shared/components/UI/CardForm.vue";
import CardDivider from "../../../shared/components/UI/CardDivider.vue";
import InvoiceReportTable from "../InvoiceReport/InvoiceReportTable.vue";
import { COLUMNS } from "./CustomerShowReportColumns";
import CompanyStore from "../../../shared/components/Company/CompanyStore";
import store from "./CustomerStore";
import CustomerMixin from "./CustomerMixin";

const responseStore = {
  response: null,
  reportItemsResponse: null
};

const fetchResource = async (to, from, next) => {
  responseStore.response = await store.fetch(to.params.id);

  responseStore.reportItemsResponse = await store.fetchReportItems(
    CompanyStore.currentCompany.id,
    to.params.id
  );

  next();
};

export default {
  name: "CustomerShow",

  title () {
    return `Customer - ${Dashboard.currentTenant.name}`;
  },

  components: {
    FormCard,
    CardForm,
    CardDivider,
    InvoiceReportTable
  },

  mixins: [CustomerMixin],

  beforeRouteEnter: fetchResource,
  beforeRouteUpdate: fetchResource,

  data: function () {
    return {
      responseStore: responseStore,
      store: store,
      customer: responseStore.response.resource,
      columns: COLUMNS,
      items: responseStore.reportItemsResponse.invoice_reports
    };
  },

  computed: {
    customerFullName () {
      return `${this.customer.first_name} ${this.customer.last_name}`;
    },

    showBillToAddress () {
      return this.customer.billing_address.city || this.customer.billing_address.country;
    },

    showShipToAddress () {
      return this.customer.shipping_address.city || this.customer.shipping_address.country;
    },

    customerHasShippingInfo () {
      return this.customer.shipping_address.address ||
             this.showShipToAddress ||
             this.customer.shipping_address.phone_number ||
             this.customer.shipping_address.attention;
    }
  },

  mounted () {
    this.addBreadcrumb(this.customer.display_name);
  },

  methods: {
    displayAddress (zip, city, country) {
      if (city && country) {
        return `${zip} ${city}, ${country}`.trim();
      } else {
        return `${zip} ${city}${country}`.trim();
      }
    },

    async deleteCustomer () {
      $("#delete-customer-modal").modal("hide");
      this.handleResponseToIndex(await store.delete(this.customer.id, this.$route.params));
    }
  }
};
</script>
